import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { InfoboxComponent, InfoboxType } from '@core/components-infobox-legacy';
import { injectTranslate } from '@core/i18n-legacy';
import { IntercomService } from '@core/intercom-integration-legacy';
import { CompanyVerificationDataService } from '@core/tellent-company-verification-logic';
import { CompanyVerificationRequestStatus } from '@core/tellent-company-verification-types';
import { ButtonComponent, DialogService, DialogWidth, IconName } from '@core/user-interface-legacy';

import { CompanyVerificationDialogComponent } from '../company-verification-dialog/company-verification-dialog.component';

interface CompanyVerificationInfoboxI {
  infoboxType: InfoboxType;
  iconName: IconName;
  heading: string;
  subheading: string;
  ctaLabel?: string;
}

@Component({
  selector: 'tlc-company-verification-infobox',
  templateUrl: './company-verification-infobox.component.html',
  styleUrls: ['./company-verification-infobox.component.less'],
  imports: [ButtonComponent, InfoboxComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyVerificationInfoboxComponent {
  private readonly intercom = inject(IntercomService);
  private readonly dialogService = inject(DialogService);
  private readonly t = injectTranslate();

  public readonly companyVerificationStatus = toSignal(
    inject(CompanyVerificationDataService).status$,
  );

  public readonly shouldShowVerificationInfobox = computed<boolean>(
    () => this.companyVerificationStatus() !== CompanyVerificationRequestStatus.Approved,
  );

  public readonly verificationStatusMap = new Map<
    CompanyVerificationRequestStatus,
    CompanyVerificationInfoboxI
  >([
    [
      CompanyVerificationRequestStatus.Pending,
      {
        infoboxType: 'warning',
        iconName: 'warning',
        heading: this.t('tlc.company_verification.infobox.pending.heading'),
        subheading: this.t('tlc.company_verification.infobox.pending.subheading'),
        ctaLabel: this.t('tlc.company_verification.infobox.pending.cta_label'),
      },
    ],
    [
      CompanyVerificationRequestStatus.FormSubmitted,
      {
        infoboxType: 'information',
        iconName: 'info',
        heading: this.t('tlc.company_verification.infobox.form_submitted.heading'),
        subheading: this.t('tlc.company_verification.infobox.form_submitted.subheading'),
      },
    ],
    [
      CompanyVerificationRequestStatus.Rejected,
      {
        infoboxType: 'error',
        iconName: 'error',
        heading: this.t('tlc.company_verification.infobox.rejected.heading'),
        subheading: this.t('tlc.company_verification.infobox.rejected.subheading'),
        ctaLabel: this.t('tlc.company_verification.infobox.rejected.cta_label'),
      },
    ],
  ]);

  public verificationStatus = input<string>();

  public ctaButtonClicked(): void {
    const verificationStatus = this.companyVerificationStatus();

    switch (verificationStatus) {
      case CompanyVerificationRequestStatus.Pending:
        this.openVerificationDialog();
        break;
      case CompanyVerificationRequestStatus.Rejected:
        this.openIntercom();
        break;
      case CompanyVerificationRequestStatus.Approved:
      case CompanyVerificationRequestStatus.FormSubmitted:
      default:
        break;
    }
  }

  private openVerificationDialog(): void {
    this.dialogService.openCustom(
      CompanyVerificationDialogComponent,
      {
        width: DialogWidth.SmallXX,
      },
      {},
    );
  }

  private openIntercom(): void {
    this.intercom.startNewMessage(
      this.t('tlc.company_verification.status.rejected.intercom_message'),
    );
  }
}
